import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Switch,
  Space,
  message,
  Row,
  Col,
  Typography,
  Breadcrumb,
  InputNumber,
  Spin
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useFixedFooter, useFixedFooterPlaceholder } from '../hooks/useFixedFooter';
import weworkGroupJoinWayService from '../services/weworkGroupJoinWayService';
import weworkService from '../services/weworkService';
import './WeworkGroupJoinWay.css';
import GroupSelect from '../components/common/GroupSelect';

const { Title } = Typography;

const WeworkGroupJoinWayCreate = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [autoCreateRoom, setAutoCreateRoom] = useState(true);
  
  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 加载数据
  useEffect(() => {
    loadInitialData();
    if (isEdit) {
      loadGroupJoinWayDetail();
    }
  }, [id]);

  // 加载初始数据
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // 加载群组列表
      const groupsData = await weworkService.getAllGroups();
      console.log('🔍 获取到的群组数据:', groupsData);
      
      let processedGroups = [];
      if (Array.isArray(groupsData)) {
        processedGroups = groupsData;
      } else if (groupsData && groupsData.content) {
        processedGroups = groupsData.content;
      } else if (groupsData && Array.isArray(groupsData.data)) {
        processedGroups = groupsData.data;
      }

      console.log('🔍 处理后的群组数据:', processedGroups);
      setGroups(processedGroups || []);
    } catch (error) {
      console.error('加载基础数据失败:', error);
      message.error('加载基础数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载群活码详情（编辑模式）
  const loadGroupJoinWayDetail = async () => {
    try {
      const data = await weworkGroupJoinWayService.getGroupJoinWayDetailById(id);
      if (data.success) {
        const groupJoinWay = data.data;
        
        // 设置表单值
        form.setFieldsValue({
          remark: groupJoinWay.remark,
          autoCreateRoom: groupJoinWay.autoCreateRoom,
          roomBaseName: groupJoinWay.roomBaseName,
          roomBaseId: groupJoinWay.roomBaseId,
          chatIdList: groupJoinWay.chatIdList
        });

        setAutoCreateRoom(groupJoinWay.autoCreateRoom);
      } else {
        message.error(data.message || '加载群活码详情失败');
        navigate('/wework-group-join-way');
      }
    } catch (error) {
      console.error('加载群活码详情失败:', error);
      message.error('加载群活码详情失败');
      navigate('/wework-group-join-way');
    }
  };

  // 处理表单提交
  const handleSubmit = async (values) => {
    setSaving(true);
    
    try {
      console.log('🚀 提交群活码数据:', values);

      // 构建提交数据
      const submitData = {
        scene: 2, // 固定为二维码插件
        remark: values.remark?.trim() || '',
        autoCreateRoom: values.autoCreateRoom,
        chatIdList: values.chatIdList || []
      };

      // 只有在自动建群时才提交群名配置
      if (values.autoCreateRoom) {
        if (values.roomBaseName?.trim()) {
          submitData.roomBaseName = values.roomBaseName.trim();
        }
        if (values.roomBaseId !== undefined && values.roomBaseId !== null) {
          submitData.roomBaseId = values.roomBaseId;
        }
      }

      // state由系统自动生成，不需要用户输入

      console.log('📤 最终提交数据:', submitData);

      let result;
      if (isEdit) {
        result = await weworkGroupJoinWayService.updateGroupJoinWay(id, submitData);
      } else {
        result = await weworkGroupJoinWayService.createGroupJoinWay(submitData);
      }

      if (result.success) {
        message.success(isEdit ? '群活码更新成功' : '群活码创建成功');
        navigate('/wework-group-join-way');
      } else {
        message.error(result.message || (isEdit ? '更新失败' : '创建失败'));
      }
    } catch (error) {
      console.error(isEdit ? '更新群活码失败:' : '创建群活码失败:', error);
      const errorMessage = error.response?.data?.message || error.message || (isEdit ? '更新失败' : '创建失败');
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // 处理表单验证失败
  const handleSubmitFailed = (errorInfo) => {
    console.log('表单验证失败:', errorInfo);
    message.error('请检查表单填写是否正确');
    
    // 滚动到第一个错误字段
    const firstErrorField = errorInfo.errorFields[0];
    if (firstErrorField) {
      form.scrollToField(firstErrorField.name[0]);
    }
  };

  // 处理自动建群开关变化
  const handleAutoCreateRoomChange = (checked) => {
    setAutoCreateRoom(checked);
    if (!checked) {
      // 关闭自动建群时清空相关字段
      form.setFieldsValue({
        roomBaseName: undefined,
        roomBaseId: undefined
      });
    }
  };

  // 返回上一页
  const handleGoBack = () => {
    navigate('/wework-group-join-way');
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Breadcrumb>
            <Breadcrumb.Item onClick={handleGoBack}>
              <Title level={5} style={{ cursor: 'pointer', color: '#1677ff', margin: 0 }}>社群活码</Title>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Title level={5} style={{ margin: 0 }}>{isEdit ? '编辑群活码' : '创建群活码'}</Title>
            </Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={handleSubmitFailed}
          scrollToFirstError
          className="contact-way-form"
          initialValues={{
            autoCreateRoom: true
          }}
        >
          <Title level={5} style={{ marginBottom: '24px' }}>群活码配置</Title>
          
          <Form.Item
            label="活码名称"
            name="remark"
            rules={[
              { required: true, message: '请输入活码名称' },
              { max: 30, message: '名称不能超过30个字符' }
            ]}
          >
            <Input 
              placeholder="请输入活码名称，用于内部管理识别" 
              maxLength={30}
              showCount
              style={{ width: '40%' }}
            />
          </Form.Item>

          <Form.Item
            label="选择社群"
            name="chatIdList"
            rules={[
              { required: true, message: '请至少选择一个社群' },
              { 
                type: 'array', 
                min: 1, 
                max: 5
              }
            ]}
          >
            <GroupSelect
              groups={groups}
              mode="multiple"
              placeholder="请选择要使用此活码的社群（最多5个）"
              style={{ width: '40%' }}
              // 接口返回的群ID字段为 chatId
              valueKey="chatId"
              labelKey="name"
              ownerNameKey="ownerName"
              valueType="raw"
            />
          </Form.Item>

          <Form.Item
            label="自动建群"
            name="autoCreateRoom"
            valuePropName="checked"
            help="当所选群组都满员时，是否自动创建新的群"
          >
            <Switch 
              checkedChildren="开启" 
              unCheckedChildren="关闭" 
              onChange={handleAutoCreateRoomChange}
            />
          </Form.Item>

          {autoCreateRoom && (
            <>
              <Form.Item
                label="群名前缀"
                name="roomBaseName"
                help="自动创建群的名称前缀，最长40个字符"
              >
                <Input 
                  placeholder="例如：产品交流群、技术支持群" 
                  maxLength={40}
                  showCount
                  style={{ width: '40%' }}
                />
              </Form.Item>

              <Form.Item
                label="群起始序号"
                name="roomBaseId"
                help="群名的起始编号"
              >
                <InputNumber 
                  min={1} 
                  max={999999} 
                  placeholder="1" 
                  style={{ width: '200px' }}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Card>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={handleGoBack}>
            取消
          </Button>
          <Button type="primary" onClick={() => form.submit()} loading={saving} icon={<SaveOutlined />} size="middle">
            {saving ? '保存中...' : (isEdit ? '保存修改' : '创建群活码')}
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default WeworkGroupJoinWayCreate;